import Vue from 'vue';
import firebase from "@/plugins/firebase";
import { database } from "@/plugins/firebase";
import moment from "moment";
import store from '@/store/index';
import _ from "lodash";
import { CURRENT_SEASON_YEAR } from "@/store/seasonYear";
import { logActivity } from '@/services/ActivityLogService';
const SpecialOperatingHoursCollection = 'specialOperatingHours' + CURRENT_SEASON_YEAR;
export const SpecialOperatingHoursModule = {
    namespaced: true,
    state: {
        future: [],
        old: [],
    },
    mutations: {
        store(state, specialOperatingHour) {
            if (moment().isSameOrBefore(specialOperatingHour.data.validFrom)) {
                const x = state.future.find(x => x.id === specialOperatingHour.id);
                if (x) {
                    if (_.isEqual(x, specialOperatingHour)) {
                        return;
                    }
                    state.future.splice(state.future.indexOf(x), 1, specialOperatingHour);
                    store.dispatch('specialOperatingHours/storeCache');
                    return;
                }
                state.future.push(specialOperatingHour);
            }
            else {
                const x = state.old.find(x => x.id === specialOperatingHour.id);
                if (x) {
                    if (_.isEqual(x, specialOperatingHour)) {
                        return;
                    }
                    state.old.splice(state.old.indexOf(x), 1, specialOperatingHour);
                    store.dispatch('specialOperatingHours/storeCache');
                    return;
                }
                state.old.push(specialOperatingHour);
            }
            store.dispatch('specialOperatingHours/storeCache');
        },
        update(state, specialOperatingHours) {
            state.future.find((oldSpecialOperatingHours, index) => {
                if (oldSpecialOperatingHours.id === specialOperatingHours.id) {
                    Vue.set(state, index, specialOperatingHours);
                    return true;
                }
            });
        },
        destroy(state, specialOperatingHours) {
            state.future.splice(state.future.findIndex(item => specialOperatingHours.id === item.id), 1);
        },
        clear(state) {
            state = {
                future: [],
                old: [],
            };
        },
    },
    actions: {
        async store(context, specialOperatingHours) {
            specialOperatingHours.data.pauses.sort();
            await database.collection(SpecialOperatingHoursCollection).add(specialOperatingHours.data);
            // Log activity (fire-and-forget)
            logActivity('CREATE', 'operatingHours', specialOperatingHours.id || 'new', null, specialOperatingHours.data);
        },
        async update(context, specialOperatingHours) {
            specialOperatingHours.data.pauses.sort();
            let document = await database.collection(SpecialOperatingHoursCollection).doc(specialOperatingHours.id);
            await document.update(specialOperatingHours.data);
            // Log activity (fire-and-forget)
            logActivity('UPDATE', 'operatingHours', specialOperatingHours.id, null, specialOperatingHours.data);
        },
        async destroy(context, specialOperatingHours) {
            // Log activity (fire-and-forget)
            logActivity('DELETE', 'operatingHours', specialOperatingHours.id, specialOperatingHours.data, null);
            // Soft delete - set timestamp
            specialOperatingHours.data.deletedAt = firebase.firestore.Timestamp.now();
            // Update document (not delete)
            const doc = database.collection(SpecialOperatingHoursCollection).doc(specialOperatingHours.id);
            await doc.update({ deletedAt: specialOperatingHours.data.deletedAt });
            // Update local state and cache
            context.commit('destroy', specialOperatingHours);
            context.dispatch('storeCache');
        },
        async listen(context) {
            context.commit('clear');
            //await context.dispatch('loadCache');
            return database.collection(SpecialOperatingHoursCollection).onSnapshot(snapshot => {
                for (let change of snapshot.docChanges()) {
                    const docData = change.doc.data();
                    // Skip soft-deleted documents
                    if (docData.deletedAt) {
                        if (change.type === 'modified') {
                            // Document was soft-deleted, remove from local state
                            context.commit('destroy', { id: change.doc.id, data: docData });
                            context.dispatch('storeCache');
                        }
                        // Skip 'added' for already soft-deleted documents
                        continue;
                    }
                    switch (change.type) {
                        case 'added':
                            context.commit('store', { id: change.doc.id, data: docData });
                            break;
                        case 'modified':
                            context.commit('update', { id: change.doc.id, data: docData });
                            context.dispatch('storeCache');
                            break;
                        case 'removed':
                            context.commit('destroy', { id: change.doc.id, data: docData });
                            context.dispatch('storeCache');
                            break;
                    }
                }
            });
        },
        async storeCache(context) {
            localStorage.removeItem(SpecialOperatingHoursCollection);
            const operatingHours = [];
            for (const hour of [...context.state.future, ...context.state.old]) {
                operatingHours.push(hour);
            }
            try {
                localStorage.setItem(SpecialOperatingHoursCollection, JSON.stringify(operatingHours));
            }
            catch (e) { }
        },
        async loadCache(context) {
            const cache = localStorage.getItem(SpecialOperatingHoursCollection);
            if (!cache) {
                return;
            }
            const specialOperatingHours = JSON.parse(cache);
            for (const specialOperatingHour of specialOperatingHours) {
                context.commit('store', { id: specialOperatingHour.id, data: specialOperatingHour.data });
            }
        },
    },
    getters: {
        // Active entities only (for selection dropdowns) - deletedAt is null, undefined, or missing
        active(state) {
            const combined = [...state.future, ...state.old];
            return combined.filter(h => !h.data.deletedAt);
        },
        // All entities including deleted (for historical views)
        all(state) {
            return [...state.future, ...state.old];
        },
        // Deleted entities only (for admin filter)
        deleted(state) {
            const combined = [...state.future, ...state.old];
            return combined.filter(h => !!h.data.deletedAt);
        },
        // Active first, then deleted (for management list per FR-010)
        allSortedForManagement(state) {
            const combined = [...state.future, ...state.old];
            const active = combined.filter(h => !h.data.deletedAt);
            const deletedItems = combined.filter(h => !!h.data.deletedAt);
            return [...active, ...deletedItems];
        },
        // Future operating hours only (original getter renamed for clarity)
        future(state) {
            return state.future;
        },
        // Old/past operating hours only
        old(state) {
            return state.old;
        }
    }
};
